import { Box } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import { CHECKBOX_SIZE_CLASS, Checkbox } from "../../components/ui/checkbox";
import { Icon, type GlyphName } from "../../components/ui/icon";
import { Text } from "../../components/ui/text";
import {
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  WASH_HOVER,
  WASH_SELECTED,
} from "../../components/ui/box-classes";
import { cn } from "../../lib/utils";
import {
  EntityAvatar,
  EntityAvatarFallback,
  EntityAvatarImage,
} from "../entity-group";
import * as React from "react";
import { useEntitySelector, type EntityItemConfig } from "./entity-selector-context";
import type { Omittable } from "../../lib/props";

/**
 * A list's row rhythm. `"default"` is the 32px rung: a 24px visual rail plus
 * 4px above and below, the height of the section labels and the search field.
 * `"compact"` is 28px, 2px around the same rail. `"spacious"` takes 8px (6px
 * once a pointer is likely, sm+).
 */
export type EntitySelectorRowRhythm = "compact" | "default" | "spacious";

const ROW_RHYTHM_CLASSES: Record<EntitySelectorRowRhythm, string> = {
  compact: "py-0.5",
  default: "py-1",
  spacious: "py-2 sm:py-1.5",
};

function itemClasses({
  highlighted,
  active,
  disabled,
  rhythm,
}: {
  highlighted: boolean;
  active: boolean;
  disabled: boolean;
  rhythm: EntitySelectorRowRhythm;
}): string {
  // mx-1.5 insets the rounded hover wash 6px from the popover edge — the same
  // 6px as the list's top and bottom gutter, so the wash floats evenly on all
  // sides. px-1 matches the row's 4px above and below, and together they put
  // content on the list's 10px reading edge (see entity-selector-layout).
  return cn(
    "group relative flex flex-row items-center gap-3 rounded-md mx-1.5 px-1 cursor-pointer",
    // Row rhythm is a property of the LIST, not of the row: the pool sets it
    // once for every row it renders (see `EntitySelectorRowRhythm`).
    ROW_RHYTHM_CLASSES[rhythm],
    WASH_HOVER,
    (highlighted || active) && WASH_SELECTED,
    disabled && "opacity-64",
  );
}

function subtitleClasses(hideOnHover: boolean, truncatesFirst: boolean): string {
  return cn(
    "text-xs text-muted-foreground",
    truncatesFirst ? "min-w-0 shrink" : "shrink-0",
    // The floating actions take the subtitle's place while they show: on
    // hover, and on the row a held press ARMED (`lib/touch-arm`).
    hideOnHover && "group-hover/option:hidden group-data-armed/option:hidden",
  );
}

// The action buttons float over the row's right edge and are the row's
// hover-revealed chrome (`hoverReveal="option"` on the cluster, the row being
// the `group="option"`): in on hover, held by keyboard focus and an open
// popup, and on a coarse pointer revealed by a HELD press that arms the row —
// one row's buttons at a time, never a standing column of the same button on
// every row. The press that arms is spent, so the row does not toggle.
const ACTIONS_POSITION_CLASSES = "absolute right-2 top-1/2 -translate-y-1/2";

const PADDED_ICON_CLASSES = "p-1 rounded-md";

// Trailing chrome sits in row flow (no overlay — it must not cover the title)
// and is revealed the same way the actions are (`hoverReveal="option"`): on
// hover, by a held press on a coarse pointer, and pinned by an open popup —
// Base UI stamps `data-popup-open` on the trigger, so the editor a trailing
// button opened survives the pointer leaving the row.

// The reorder grip overlays the row's leading gutter (rows opt into the
// gutter via GRIP_ROW_CLASSES so the grip has real space) and reveals on
// hover, matching the trailing chrome's rhythm.
const GRIP_CLASSES =
  "absolute left-1 top-1/2 -translate-y-1/2 cursor-grab touch-none";
// Hover-revealed grip: hidden at rest, in on approach, always on touch.
const GRIP_HOVER_CLASSES =
  "opacity-0 group-hover:opacity-100 pointer-coarse:opacity-100";
const GRIP_ROW_CLASSES = "pl-6";

const ItemActionButton = ({
  label,
  tooltip,
  onClick,
  variant,
}: Omittable<{
  label: string;
  tooltip?: string;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  variant: "outline" | "solid";
}>) => {
  const button = (
    <Button
      variant={variant}
      size="xs"
      shape="square"
      onClick={onClick}
      tabIndex={-1}
    >
      {label}
    </Button>
  );
  if (!tooltip) return button;
  return (
    <Tooltip>
      <TooltipTrigger render={button} />
      <TooltipPopup side="top">
        <Text size="xs">{tooltip}</Text>
      </TooltipPopup>
    </Tooltip>
  );
};

const ItemSubtitle = ({
  hideOnHover,
  truncatesFirst,
  ...props
}: {
  hideOnHover?: boolean;
  truncatesFirst?: boolean;
  children: React.ReactNode;
} & Omit<React.ComponentProps<typeof Text>, "className">) => (
  <Text
    className={subtitleClasses(!!hideOnHover, !!truncatesFirst)}
    {...props}
  />
);

export type EntitySelectorItemProps = Omittable<{
  entity: EntityItemConfig;
  poolId: string;
  onAction?: (entity: EntityItemConfig) => void;
  actionLabel?: string;
  /** Tooltip on the primary action button (the pool badge's `tooltip`). */
  actionTooltip?: string;
  secondaryActionLabel?: string;
  onSecondaryAction?: (entity: EntityItemConfig) => void;
  showCheckbox?: boolean;
  hideSelectionHighlight?: boolean;
  /** Drop the single-select row's trailing check — for a picker whose
   *  trigger already shows the value, so a mark in the list says it twice. */
  hideSelectedCheck?: boolean;
  /** Optional label shown after the entity title to indicate which pool an item belongs to. */
  poolLabel?: string;
  /** Hide the avatar/icon column. @default false */
  hideAvatar?: boolean;
  /**
   * The row's vertical rhythm. The pool sets it for every row from its own
   * `rhythm` prop, or from whether its items carry sections. @default "default"
   */
  rhythm?: EntitySelectorRowRhythm;
  /** Whether this is the active (highlighted) item — independent of selection. */
  active?: boolean;
  /** Index of this item within the pool's filtered list. */
  index?: number;
  /** Make this item active (hover and keyboard both drive it). */
  onActivate?: (index: number) => void;
  /** Render the reorder grip in the row's leading gutter (`data-esgrip`) —
   *  set by a pool whose config carries `onReorder`. */
  grip?: boolean;
  /** Keep the grip standing instead of hover-revealed (pool
   *  `gripVisibility: "always"`). */
  gripStanding?: boolean;
  /** Reserve the grip gutter without drawing a grip — a non-reorderable row
   *  inside a reorderable pool, so its content lines up with its neighbours. */
  gripGutter?: boolean;
  /** Fade the row while it is the one being drag-reordered. */
  dimmed?: boolean;
  ref?: React.Ref<HTMLDivElement>;
}>;

function EntitySelectorItemInner({
  entity,
  poolId,
  onAction,
  actionLabel,
  actionTooltip,
  secondaryActionLabel,
  onSecondaryAction,
  showCheckbox = true,
  hideSelectionHighlight = false,
  hideSelectedCheck = false,
  poolLabel,
  hideAvatar = false,
  rhythm = "default",
  active = false,
  index,
  onActivate,
  grip = false,
  gripStanding = false,
  gripGutter = false,
  dimmed = false,
  ref,
}: EntitySelectorItemProps) {
  const { toggleSelection, isSelected, config } = useEntitySelector();
  const selected = isSelected(entity.id);
  const disabled = entity.disabled ?? false;
  const singleSelect = config.maxSelections === 1;
  const titleWeight = config.itemTitleWeight ?? "medium";

  const handleClick = React.useCallback(() => {
    if (disabled) return;
    toggleSelection(entity, poolId);
  }, [disabled, entity, poolId, toggleSelection]);

  const handleAction = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      onAction?.(entity);
    },
    [entity, onAction],
  );

  const handleSecondaryAction = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      onSecondaryAction?.(entity);
    },
    [entity, onSecondaryAction],
  );

  const hasActions =
    (actionLabel && onAction) || (secondaryActionLabel && onSecondaryAction);

  const handlePointerEnter = React.useCallback(() => {
    if (index != null) onActivate?.(index);
  }, [index, onActivate]);

  const avatarFallback = React.useMemo(() => {
    if (entity.avatar?.fallback) return entity.avatar.fallback;
    const [firstWord, secondWord] = entity.title
      .split(" ")
      .filter((w) => w.length > 0);
    if (firstWord && secondWord) {
      return `${firstWord.slice(0, 1)}${secondWord.slice(0, 1)}`.toUpperCase();
    }
    // A titleless row has no initials — null sends the render to the glyph
    // branch (the person glyph by default), never punctuation.
    return entity.title.trim().slice(0, 2).toUpperCase() || null;
  }, [entity.avatar?.fallback, entity.title]);

  const avatarIcon = entity.avatar?.icon ?? "user";
  const avatarColor = entity.avatar?.color;

  const row = (
    <Box
      ref={ref}
      direction="row"
      // The row is the hover/arm scope for its chrome: a held press on a
      // coarse pointer arms it (wash + actions) and is spent; a tap presses.
      group="option"
      onClick={disabled ? undefined : handleClick}
      onPointerEnter={onActivate ? handlePointerEnter : undefined}
      id={`entity-item-${entity.id}`}
      tabIndex={-1}
      role="option"
      // A rendered body replaces the title on screen; the title stays the
      // row's name for assistive tech and role queries.
      aria-label={entity.body ? entity.title : undefined}
      aria-selected={selected}
      aria-disabled={disabled || undefined}
      data-entity-id={entity.id}
      className={cn(
        itemClasses({
          highlighted: selected && !hideSelectionHighlight,
          active,
          disabled,
          rhythm,
        }),
        (grip || gripGutter) && GRIP_ROW_CLASSES,
        dimmed && "opacity-30",
      )}
    >
      {grip && (
        <Box
          data-esgrip=""
          aria-hidden
          direction="row"
          align="center"
          className={cn(GRIP_CLASSES, !gripStanding && GRIP_HOVER_CLASSES)}
          onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
        >
          <Icon name="grip-vertical" size="sm" color="muted" />
        </Box>
      )}
      {!singleSelect &&
        showCheckbox &&
        (entity.actionRow ? (
          // An action row holds the column open rather than drawing a box
          // nobody can tick — dropping the width instead would start its glyph
          // and label a rail left of every row above it.
          <Box aria-hidden shrink={false} className={CHECKBOX_SIZE_CLASS} />
        ) : (
          // The row is the control: it carries `role="option"` and
          // `aria-selected` and it owns the press. The box only draws that state.
          <Checkbox checked={selected} disabled={disabled} nonInteractive />
        ))}

      {entity.rowLead && (
        <Box shrink={false} direction="row" align="center">
          {entity.rowLead()}
        </Box>
      )}

      {!hideAvatar &&
        !entity.body &&
        (entity.avatar?.render ? (
          entity.avatar.render()
        ) : entity.avatar?.padded && entity.avatar?.icon ? (
          <Box
            data-color={avatarColor}
            className={cn(
              PADDED_ICON_CLASSES,
              avatarColor ? "bg-c-soft" : "bg-muted/15",
            )}
          >
            <Icon
              {...(typeof avatarIcon === "string"
                ? { name: avatarIcon as GlyphName }
                : { as: avatarIcon })}
              size="md"
              color={avatarColor}
            />
          </Box>
        ) : entity.avatar?.icon && !entity.avatar?.src ? (
          // Un-padded icon with no image = a BARE glyph: colored ink on the
          // visual rail, no tile and no avatar circle (custom-field category
          // glyphs). Centered in the padded tile's 24px box so mixed lists
          // keep one rail. An icon WITH `src` stays on the avatar branch —
          // there the icon is the image's fallback, not the visual itself.
          <Box
            direction="row"
            align="center"
            justify="center"
            width={24}
            height={24}
            shrink={false}
          >
            <Icon
              {...(typeof avatarIcon === "string"
                ? { name: avatarIcon as GlyphName }
                : { as: avatarIcon })}
              size="md"
              color={avatarColor ?? "muted"}
            />
          </Box>
        ) : (
          // Decorative: the title beside it names the option, and an alt
          // here would say the name twice ("Alice Smith Alice Smith").
          <EntityAvatar
            size="sm"
            aria-hidden
            muted={entity.muted}
            invited={entity.invited}
          >
            {entity.avatar?.src ? (
              <EntityAvatarImage alt="" src={entity.avatar.src} />
            ) : null}
            <EntityAvatarFallback
              // The colour is a NAME, so it travels as data and the cascade
              // does the lookup — same as the padded branch above. Writing it
              // to `backgroundColor` painted the CSS keyword that happened to
              // share the name (`blue`) and nothing at all for the ones that
              // do not (`primary`, `light-purple`).
              data-color={avatarColor}
              className={cn(
                avatarColor
                  ? "bg-c text-c-foreground"
                  : "bg-muted text-muted-foreground",
              )}
            >
              {entity.avatar?.icon || !avatarFallback ? (
                <Icon
                  {...(typeof avatarIcon === "string"
                    ? { name: avatarIcon as GlyphName }
                    : { as: avatarIcon })}
                  size="lg"
                  color="muted"
                />
              ) : (
                <Text size="xs" weight="bold" variant="muted">
                  {avatarFallback}
                </Text>
              )}
            </EntityAvatarFallback>
          </EntityAvatar>
        ))}

      {entity.body ? (
        <Box direction="row" align="center" flex={1} minWidth={0}>
          {entity.body()}
        </Box>
      ) : (
        <Box direction="row" align="baseline" flex={1} minWidth={0} gap="sm">
          <ItemTexts
            entity={entity}
            hasActions={!!hasActions}
            poolLabel={poolLabel}
            titleWeight={titleWeight}
          />
        </Box>
      )}

      {entity.rowMeta && (
        <Box shrink={false} direction="row" align="center" gap="sm">
          {entity.rowMeta()}
        </Box>
      )}

      {singleSelect && selected && !hideSelectedCheck && (
        <Box shrink={false}>
          <Icon name="check" size="lg" color="primary" />
        </Box>
      )}

      {entity.trailing && (
        <Box
          shrink={false}
          direction="row"
          align="center"
          hoverReveal="option"
          // Hover chrome: a pointer shortcut, hidden from assistive tech so
          // the option's name stays the entity's title.
          aria-hidden
          onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
        >
          {entity.trailing()}
        </Box>
      )}

      {hasActions && (
        <Box
          direction="row"
          align="center"
          gap="xs"
          hoverReveal="option"
          aria-hidden
          className={ACTIONS_POSITION_CLASSES}
        >
          {secondaryActionLabel && onSecondaryAction && (
            <ItemActionButton
              label={secondaryActionLabel}
              onClick={handleSecondaryAction}
              variant="solid"
            />
          )}
          {actionLabel && onAction && (
            <ItemActionButton
              label={actionLabel}
              tooltip={actionTooltip}
              onClick={handleAction}
              variant="outline"
            />
          )}
        </Box>
      )}
    </Box>
  );

  if (!entity.tooltip) return row;
  return (
    <Tooltip>
      <TooltipTrigger render={row} />
      <TooltipPopup side="right">
        <Text size="xs">{entity.tooltip}</Text>
      </TooltipPopup>
    </Tooltip>
  );
}

function ItemTexts({
  entity,
  hasActions,
  poolLabel,
  titleWeight,
}: Omittable<{
  entity: EntityItemConfig;
  hasActions: boolean;
  poolLabel?: string;
  titleWeight: "normal" | "medium";
}>) {
  // On one baseline the description is the part that gives. A title crushed to
  // "Ridgev…" beside a street address printed in full is the wrong trade, so the
  // title holds up to half the row and the description truncates into the rest.
  const sharesTheLine = !!entity.description;
  return (
    <>
      <Text
        size="sm"
        weight={titleWeight}
        truncate
        variant={entity.muted ? "muted" : undefined}
        className={sharesTheLine ? "max-w-1/2 shrink-0" : "min-w-0 shrink"}
      >
        {entity.title}
      </Text>
      {!!entity.description && (
        <ItemSubtitle
          hideOnHover={hasActions}
          truncate
          truncatesFirst={sharesTheLine}
        >
          {entity.description}
        </ItemSubtitle>
      )}
      {!!poolLabel && <ItemSubtitle truncate>{poolLabel}</ItemSubtitle>}
    </>
  );
}

EntitySelectorItemInner.displayName = "EntitySelectorItemInner";

const EntitySelectorItem = React.memo(EntitySelectorItemInner, (prev, next) => {
  return (
    prev.entity.id === next.entity.id &&
    prev.entity.title === next.entity.title &&
    prev.entity.description === next.entity.description &&
    prev.entity.tooltip === next.entity.tooltip &&
    prev.entity.disabled === next.entity.disabled &&
    prev.entity.muted === next.entity.muted &&
    prev.entity.invited === next.entity.invited &&
    prev.entity.avatar?.src === next.entity.avatar?.src &&
    prev.entity.avatar?.fallback === next.entity.avatar?.fallback &&
    prev.entity.avatar?.icon === next.entity.avatar?.icon &&
    prev.entity.avatar?.color === next.entity.avatar?.color &&
    prev.entity.avatar?.padded === next.entity.avatar?.padded &&
    prev.entity.avatar?.render === next.entity.avatar?.render &&
    prev.entity.actionRow === next.entity.actionRow &&
    prev.entity.rowLead === next.entity.rowLead &&
    prev.entity.rowMeta === next.entity.rowMeta &&
    prev.entity.trailing === next.entity.trailing &&
    prev.entity.body === next.entity.body &&
    prev.grip === next.grip &&
    prev.gripStanding === next.gripStanding &&
    prev.gripGutter === next.gripGutter &&
    prev.dimmed === next.dimmed &&
    prev.poolId === next.poolId &&
    prev.actionLabel === next.actionLabel &&
    prev.actionTooltip === next.actionTooltip &&
    prev.secondaryActionLabel === next.secondaryActionLabel &&
    prev.showCheckbox === next.showCheckbox &&
    prev.hideSelectionHighlight === next.hideSelectionHighlight &&
    prev.hideSelectedCheck === next.hideSelectedCheck &&
    prev.onAction === next.onAction &&
    prev.onSecondaryAction === next.onSecondaryAction &&
    prev.poolLabel === next.poolLabel &&
    prev.hideAvatar === next.hideAvatar &&
    prev.rhythm === next.rhythm &&
    prev.active === next.active &&
    prev.index === next.index &&
    prev.onActivate === next.onActivate
  );
});

EntitySelectorItem.displayName = "EntitySelectorItem";

export { EntitySelectorItem };
